import React from "react";
import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { AiOutlineEdit } from "react-icons/ai";
import { MdOutlineFileCopy } from "react-icons/md";
import { BsCheckCircleFill } from "react-icons/bs";
import { HashLoader } from "react-spinners";
import { RiUserSettingsLine } from "react-icons/ri";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import { MainButton } from "../../components/MainButton";
import { COLORS } from "../../utils/colors";
import {
  createOperator,
  deleteOperator,
  getOperator,
  updateOperator,
} from "../../api/operators";
import { SnackbarComponent } from "../../components/SnackbarComponent";
import { updateUser } from "../../store/mainReducer";
import { getOrgByEmail } from "../../api/user";
import { ICONS } from "../../utils/icons";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} placement="top-start" />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: COLORS.white,
    color: "rgba(0, 0, 0, 0.87)",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

export const Operators = ({ userData }) => {
  const [openEditPopup, setOpenEditPopup] = useState(false);
  const [openCreatePopup, setOpenCreatePopup] = useState(false);
  const [operatorID, setOperatorID] = useState({});
  const [apiKey, setApiKey] = useState(null);

  useEffect(() => {
    if (userData?.operators?.length > 0) {
      setApiKey(userData?.operators[0]?.api_key);
    }
  }, [userData]);

  const handleOpenEditPopup = (id) => {
    setOpenEditPopup(true);
    setOperatorID(id);
  };

  const handleOpenCreatePopup = () => {
    setOpenCreatePopup(true);
  };

  return (
    <div>
      <div style={{ height: "20px" }} />
      {openEditPopup && (
        <EditOperatorDialog
          openPopup={openEditPopup}
          setOpenPopup={setOpenEditPopup}
          userData={userData}
          operatorID={operatorID}
          apiKey={apiKey}
        />
      )}
      {openCreatePopup && (
        <CreateOperatorDialog
          openPopup={openCreatePopup}
          setOpenPopup={setOpenCreatePopup}
          apiKey={apiKey}
          userData={userData}
        />
      )}
      <div className="standard-container" style={{ marginBottom: "20px" }}>
        <h2>Manage Operators</h2>
        <p id="subTitle" style={{ marginBottom: "20px" }}>
          Keep track of your operators, their permissions and activity.
        </p>
        <MainButton
          text={"Create new operator"}
          color={COLORS.black}
          hoverColor={COLORS.black}
          textColor={COLORS.white}
          borderColor={COLORS.black}
          shadowColor={COLORS.mainAccent}
          textHoverColor={COLORS.lightGrey}
          onClickAction={() => handleOpenCreatePopup()}
          width={"fit-content"}
        >
          {ICONS.plus20}
        </MainButton>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
        {userData?.operators?.map((operator, index) => (
          <div
            key={index}
            className="small-container"
            style={{
              borderColor: index === 0 && COLORS.mainAccent,
              background: index === 0 && "#f3f8fe",
              height: "90px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "row" }}>
              <div style={{ width: "100%" }}>
                <p
                  style={{
                    fontFamily: "DrukWideCy",
                    fontSize: "18px",
                  }}
                >
                  {operator?.name}
                </p>
                <p id="subheader">
                  API Key: {operator?.api_key?.substring(0, 2)}...
                  {operator?.api_key?.substring(32)}
                </p>
              </div>
              {index === 0 ? (
                <HtmlTooltip
                  title={
                    <React.Fragment>
                      <Typography color="inherit">Definition</Typography>
                      {"The"} <b>{"primary operator"}</b>{" "}
                      {
                        " is your main operator used for all actions in the backoffice. It cannot be deleted nor edited. You can create additional operators with different permissions."
                      }
                    </React.Fragment>
                  }
                >
                  <Chip label="primary" color="primary" />
                </HtmlTooltip>
              ) : (
                <RiUserSettingsLine color={COLORS.mainAccent} />
              )}
            </div>
            <div className="container-row" style={{ marginTop: "15px" }}>
              <MainButton
                text={"Edit"}
                color={"transparent"}
                hoverColor={"transparent"}
                textColor={COLORS.black}
                borderColor={COLORS.black}
                shadowColor={COLORS.mainAccent}
                textHoverColor={COLORS.grey}
                onClickAction={() => handleOpenEditPopup(operator._id)}
                width={"fit-content"}
              >
                {<AiOutlineEdit size={20} />}
              </MainButton>
            </div>
          </div>
        ))}
      </div>
      <div style={{ height: "20px" }} />
    </div>
  );
};

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      minWidth: 250,
      fontFamily: "AuthenticSans90",
    },
  },
};

const roles = ["distributor", "promoter", "partner", "seller", "sponsor"];

const permissions = [
  {
    label: "Create event",
    value: "create_event",
  },
  {
    label: "Update event",
    value: "update_event",
  },
];

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  "& .MuiSwitch-track": {
    borderRadius: 22 / 2,
    "&:before, &:after": {
      content: '""',
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
    },
    "&:before": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    "&:after": {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "none",
    width: 16,
    height: 16,
    margin: 2,
  },
}));

const EditOperatorDialog = ({
  openPopup,
  setOpenPopup,
  operatorID,
  apiKey,
  userData,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [operator, setOperator] = useState({});
  const [copied, setCopied] = useState(false);
  const [rightsPermission, setRightsPermission] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      contact: "",
      role: "",
      allowed_endpoints: [],
    },
    validationSchema: yup.object({
      name: yup
        .string("Enter operator name")
        .required("Operator name required"),
    }),
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  useEffect(async () => {
    setLoading(true);

    try {
      const { data, error } = await getOperator(operatorID, apiKey);

      if (error) {
        handleSnackbar(error.message, "error");
        setLoading(false);
      }

      if (data) {
        setOperator(data);
      }

      setLoading(false);
    } catch (error) {
      console.log("error: ", error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      formik.setValues({
        name: operator?.name,
        role: operator?.role,
        contact: operator?.contact,
        allowed_endpoints: operator?.allowed_endpoints,
      });

      if (operator?.allowed_endpoints?.length === 0) {
        setRightsPermission(true);
      }
    } catch (error) {
      console.log("error: ", error);
      handleSnackbar(error, "error");
    }
  }, [operator]);

  const handleClose = () => {
    setOpenPopup(false);
  };

  const handleSelect = (e) => {
    if (e.target.name === "role") {
      formik.setFieldValue("role", e.target.value);
    }

    console.log("e.target.value: ", e.target.name);
    if (e.target.name === "allowed_endpoints") {
      formik.setFieldValue("allowed_endpoints", [...e.target.value]);
    }
  };

  const copyToClipboard = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };

  const handleSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message: message,
      severity: severity,
    });
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);

    try {
      /**
       * Check if there are any changes and send only updated fields to the backend
       */
      const parameters = {};
      for (const key in values) {
        if (operator[key] !== values[key]) {
          parameters[key] = values[key];
        }
      }
      parameters["operator_id"] = operatorID;
      parameters["apiKey"] = apiKey;
      /**
       * If rightsPermission is true, then the operator has all the rights
       */
      rightsPermission && (parameters["allowed_endpoints"] = []);

      const { data, error } = await updateOperator(
        parameters,
        operator.api_key
      );
      if (data) {
        handleSnackbar("Operator updated!", "success");
        setOperator(data);

        const response = await getOrgByEmail(userData?.email);

        dispatch(updateUser(response.data));
      }

      if (error) {
        console.log("error: ", error);
        handleSnackbar(error.message, "error");
      }

      setLoading(false);
    } catch (error) {
      console.log("error: ", error);
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);

    try {
      const { data, error } = await deleteOperator(operatorID, apiKey);

      if (data) {
        handleSnackbar("Operator deleted!", "success");
        setOpenPopup(false);

        const response = await getOrgByEmail(userData?.email);

        dispatch(updateUser(response.data));
      }

      if (error) {
        console.log("error: ", error);
        handleSnackbar(error.message, "error");
      }

      setLoading(false);
    } catch (error) {
      console.log("error: ", error);
      setLoading(false);
    }
  };

  return (
    <Dialog open={openPopup} onClose={() => handleClose()}>
      <SnackbarComponent snackbar={snackbar} setSnackbar={setSnackbar} />
      <DialogContent
        sx={{
          width: "400px",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <HashLoader color={COLORS.black} size={20} />
        ) : (
          <>
            <div
              className="container-column"
              style={{
                display: "flex",
                width: "-webkit-fill-available",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ marginBottom: "5px" }}>
                {ICONS.editBlack25} {operator?.name}
              </h2>
              <Chip
                sx={{
                  width: "fit-content",
                  background: COLORS.evenLighterGrey,
                  border: `1px solid ${COLORS.lightGrey}`,
                }}
                label={`API Key: ${operator?.api_key?.slice(
                  0,
                  7
                )}...${operator?.api_key?.slice(33)}`}
                icon={
                  copied ? (
                    <BsCheckCircleFill color={COLORS.mainAccent} size={16} />
                  ) : (
                    <MdOutlineFileCopy size={16} />
                  )
                }
                onClick={() => copyToClipboard(operator.api_key)}
              />
            </div>
            <div className="field-container">
              <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
                Name
              </p>
              <TextField
                fullWidth
                disabled={operator?.name === "Default operator"}
                size="small"
                className="input"
                name={"name"}
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                sx={{
                  "& label": { paddingLeft: (theme) => theme.spacing(1) },
                  "& fieldset": {
                    borderRadius: "4px",
                    border: `1px solid #c4c4c4`,
                    transition: ".2s ease-in-out all",
                  },
                }}
              />
            </div>
            <div className="field-container">
              <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
                Contact details (optional)
              </p>
              <TextField
                fullWidth
                size="small"
                className={"contact"}
                name={"contact"}
                value={formik.values.contact}
                onChange={formik.handleChange}
                error={formik.touched.contact && Boolean(formik.errors.contact)}
                helperText={formik.touched.contact && formik.errors.contact}
                sx={{
                  "& label": { paddingLeft: (theme) => theme.spacing(1) },
                  "& fieldset": {
                    borderRadius: "4px",
                    border: `1px solid #c4c4c4`,
                    transition: ".2s ease-in-out all",
                  },
                }}
              />
            </div>
            <div className="field-container">
              <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
                Role (optional)
              </p>
              <Select
                size="small"
                name="role"
                value={formik.values.role || ""}
                onChange={formik.handleChange}
                input={<OutlinedInput label="Name" />}
                MenuProps={MenuProps}
                style={{
                  maxWidth: "150px",
                  minWidth: "-webkit-fill-available",
                }}
              >
                {roles.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="container-row-full-width-space-between">
              <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
                Endpoint Permissions (optional)
              </p>
              <FormControlLabel
                control={
                  <Android12Switch
                    checked={rightsPermission}
                    onClick={() => setRightsPermission(!rightsPermission)}
                  />
                }
                label="All endpoints"
              />
            </div>
            {!rightsPermission && (
              <Select
                size="small"
                multiple
                name={"allowed_endpoints"}
                disabled={operator?.name === "Default operator"}
                value={formik?.values?.allowed_endpoints || []}
                onChange={handleSelect}
                input={<OutlinedInput label="Name" />}
                MenuProps={MenuProps}
                style={{
                  maxWidth: "150px",
                  minWidth: "-webkit-fill-available",
                }}
              >
                {permissions.map((permission) => (
                  <MenuItem key={permission.label} value={permission.value}>
                    {permission.label}
                  </MenuItem>
                ))}
              </Select>
            )}
            <div style={{ height: "20px" }} />
            <div className="container-row-full-width-space-between">
              <MainButton
                disabled={operator?.name === "Default operator"}
                color={COLORS.red}
                textColor={COLORS.white}
                textHoverColor={COLORS.lightRed}
                hoverColor={COLORS.red}
                shadowColor={COLORS.red}
                textAlignment={"center"}
                onClickAction={() => handleDelete()}
                width={"fit-content"}
              >
                {loading ? <HashLoader size={20} /> : "Delete"}
              </MainButton>
              <MainButton
                disabled={operator?.name === "Default operator"}
                color={COLORS.black}
                shadowColor={"transparent"}
                textColor={COLORS.white}
                textHoverColor={COLORS.lightGrey}
                hoverColor={COLORS.black}
                textAlignment={"center"}
                onClickAction={() => formik.handleSubmit()}
                width={"fit-content"}
              >
                {loading ? <HashLoader size={20} /> : "Save"}
              </MainButton>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CreateOperatorDialog = ({
  openPopup,
  setOpenPopup,
  apiKey,
  userData,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [fullRights, setFullRights] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      contact: "",
      role: "",
      allowed_endpoints: [],
    },
    validationSchema: yup.object({
      name: yup
        .string("Enter operator name")
        .required("Operator name required"),
    }),
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
  });

  const handleSelect = (e) => {
    if (e.target.name === "role") {
      formik.setFieldValue("role", e.target.value);
    }

    if (e.target.name === "allowed_endpoints") {
      formik.setFieldValue("allowed_endpoints", [...e.target.value]);
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      setLoading(true);

      const { data, error } = await createOperator(values, apiKey);

      if (data) {
        handleSnackbar("Operator created!", "success");

        const response = await getOrgByEmail(userData?.email);

        if (response.status === 200) {
          dispatch(updateUser(response.data));
          setSuccess(true);
        }
      }

      if (error) {
        console.log(error);
        handleSnackbar(error.message, "error");
      }

      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpenPopup(false);
  };

  const handleSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message: message,
      severity: severity,
    });
  };

  return (
    <Dialog open={openPopup} onClose={() => handleClose()}>
      <SnackbarComponent snackbar={snackbar} setSnackbar={setSnackbar} />
      <DialogContent sx={{ width: "400px" }}>
        <h2 style={{ margin: "5px 0px" }}>
          {ICONS.plusBlack25} Create Operator
        </h2>
        <div style={{ height: "20px" }} />
        <form>
          <div className="field-container">
            <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
              Name (required)
            </p>
            <TextField
              fullWidth
              size="small"
              className={"name"}
              name={"name"}
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "4px",
                  border: `1px solid #c4c4c4`,
                  transition: ".2s ease-in-out all",
                },
              }}
            />
          </div>
          <div className="field-container">
            <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
              Contact details (optional)
            </p>
            <TextField
              fullWidth
              size="small"
              className={"contact"}
              name={"contact"}
              value={formik.values.contact}
              onChange={formik.handleChange}
              error={formik.touched.contact && Boolean(formik.errors.contact)}
              helperText={formik.touched.contact && formik.errors.contact}
              sx={{
                "& label": { paddingLeft: (theme) => theme.spacing(1) },
                "& fieldset": {
                  borderRadius: "4px",
                  border: `1px solid #c4c4c4`,
                  transition: ".2s ease-in-out all",
                },
              }}
            />
          </div>
          <div className="field-container">
            <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
              Role (optional)
            </p>
            <Select
              size="small"
              name="role"
              value={formik.values.role}
              onChange={formik.handleChange}
              input={<OutlinedInput label="Name" />}
              MenuProps={MenuProps}
              style={{
                maxWidth: "150px",
                minWidth: "-webkit-fill-available",
              }}
            >
              {roles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </div>
          <div className="field-container">
            <div className="container-row-full-width-space-between">
              <p id="subheaderBlack" style={{ marginBottom: "3px" }}>
                Permissions (optional)
              </p>
              <FormControlLabel
                control={
                  <Android12Switch
                    checked={fullRights}
                    onClick={() => setFullRights(!fullRights)}
                  />
                }
                label="All endpoints"
              />
            </div>

            {!fullRights && (
              <Select
                size="small"
                multiple
                name="allowed_endpoints"
                value={formik.values.allowed_endpoints}
                onChange={handleSelect}
                input={<OutlinedInput label="Name" />}
                MenuProps={MenuProps}
                style={{
                  maxWidth: "150px",
                  minWidth: "-webkit-fill-available",
                }}
              >
                {permissions.map((permission) => (
                  <MenuItem key={permission.label} value={permission.value}>
                    {permission.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </div>
          <DialogActions>
            <MainButton
              disabled={loading || success}
              color={COLORS.black}
              shadowColor={"transparent"}
              textColor={COLORS.white}
              textHoverColor={COLORS.lightGrey}
              hoverColor={COLORS.black}
              textAlignment={"center"}
              onClickAction={() => formik.handleSubmit()}
              width={"fit-content"}
            >
              {loading ? (
                <HashLoader size={20} />
              ) : success ? (
                "Success"
              ) : (
                "Create"
              )}
            </MainButton>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};
